// =============================================================================
// File: frontend/src/contexts/TenantContext.js
// White-Label Tenant Context Provider
// Backlog #28: White-Label Admin Portal & Deployment Plan
// =============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';

const CACHE_KEY = 'healist_tenant_config';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const TenantContext = createContext(null);

// Default config (Healist branding) — used until API responds
const DEFAULT_CONFIG = {
  theme_id: null,
  theme: null,
  site_name: 'Healist.AI',
  site_tagline: 'Precision AI Health Technology\u2122',
  logo_url: null,
  favicon_url: null,
  copyright_text: '\u00a9 2026 Pivotal Technologies, LLC',
  about_url: '/healist-about.html',
  privacy_url: '/privacy-policy.html',
  terms_url: '/terms-of-service.html',
  contact_email: null,
  is_default_tenant: true,
};

/**
 * Apply theme CSS custom properties to :root
 */
function applyThemeTokens(theme) {
  if (!theme) return;

  const root = document.documentElement;
  const tokenMap = {
    '--brand-primary': theme.primary,
    '--brand-primary-light': theme.primary_light,
    '--brand-primary-dark': theme.primary_dark,
    '--brand-secondary': theme.secondary,
    '--brand-secondary-light': theme.secondary_light,
    '--brand-secondary-dark': theme.secondary_dark,
    '--font-heading': theme.font_heading,
    '--font-body': theme.font_body,
    // Derived tokens that reference primary/secondary
    '--brand-border-subtle': `rgba(${hexToRgb(theme.secondary)}, 0.2)`,
    '--sidebar-active-bg': `rgba(${hexToRgb(theme.primary)}, 0.08)`,
    '--sidebar-hover-bg': `rgba(${hexToRgb(theme.primary)}, 0.04)`,
    '--shadow-btn-primary': `0 8px 24px rgba(${hexToRgb(theme.primary)}, 0.2)`,
    '--credential-icon-bg': `rgba(${hexToRgb(theme.secondary)}, 0.15)`,
  };

  Object.entries(tokenMap).forEach(([prop, value]) => {
    if (value) root.style.setProperty(prop, value);
  });
}

/**
 * Swap Google Fonts <link> tag if theme uses different fonts
 */
function applyGoogleFonts(googleFontsUrl) {
  if (!googleFontsUrl) return;

  const existingLink = document.getElementById('tenant-google-fonts');
  if (existingLink && existingLink.href === googleFontsUrl) return;

  if (existingLink) {
    existingLink.href = googleFontsUrl;
  } else {
    const link = document.createElement('link');
    link.id = 'tenant-google-fonts';
    link.rel = 'stylesheet';
    link.href = googleFontsUrl;
    document.head.appendChild(link);
  }
}

/**
 * Convert hex color to "r, g, b" string for rgba()
 */
function hexToRgb(hex) {
  if (!hex) return '0, 0, 0';
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Read cached config from localStorage
 */
function getCachedConfig() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, ts } = JSON.parse(cached);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Write config to localStorage cache
 */
function setCachedConfig(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // localStorage full or unavailable — non-critical
  }
}

export function TenantProvider({ children }) {
  const [config, setConfig] = useState(() => {
    // Immediate: use cached config if available
    const cached = getCachedConfig();
    return cached || DEFAULT_CONFIG;
  });
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tenants/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setCachedConfig(data);

        // Apply theme if non-default tenant
        if (!data.is_default_tenant && data.theme) {
          applyThemeTokens(data.theme);
          applyGoogleFonts(data.theme.google_fonts_url);
        }

        // Update page title
        if (!data.is_default_tenant) {
          document.title = data.site_name;
        }
      }
    } catch (err) {
      // Silently fall back to cached/default config
      console.warn('Tenant config fetch failed, using defaults:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // On mount, apply cached theme immediately (prevents flash)
    const cached = getCachedConfig();
    if (cached && !cached.is_default_tenant && cached.theme) {
      applyThemeTokens(cached.theme);
      applyGoogleFonts(cached.theme.google_fonts_url);
    }

    // Then fetch fresh config
    fetchConfig();
  }, [fetchConfig]);

  const value = {
    ...config,
    loading,
    refreshConfig: fetchConfig,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;
